import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { useEscapeStack } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { ChevronDownIcon } from '#/shared/ui/icons'

type PopoverAlign = 'left' | 'center' | 'right'
type PopoverSide = 'top' | 'bottom' | 'auto'
type ResolvedSide = 'top' | 'bottom'

const VIEWPORT_MARGIN_PX = 8
const GAP_PX = 4

interface PopoverProps {
  trigger: (open: boolean) => React.ReactNode
  children:
    | React.ReactNode
    | ((close: () => void, open: boolean) => React.ReactNode)
  align?: PopoverAlign | 'auto'
  side?: PopoverSide
  animation?: PopoverAnimation
  defaultOpen?: boolean
  /**
   * Controlled open state. Pair with `onOpenChange` to lift the popover's
   * state — e.g. when a sibling control (like an `AddFilterButton`) needs
   * to re-open the same popover. When omitted, the popover falls back to
   * its own internal state seeded by `defaultOpen`.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  contentClassName?: string
  wrapperClassName?: string
  /**
   * Stretch the panel to at least the trigger's width. Sensible for
   * select-style dropdowns whose menu should line up with the field, but
   * wrong for content with its own intrinsic width (e.g. a calendar) anchored
   * to a full-width field — there it just pads the panel with empty space.
   * Defaults to `true`.
   */
  matchTriggerWidth?: boolean
  /**
   * Render the panel through a portal to `document.body` with viewport-fixed
   * positioning anchored to the trigger. Required when the popover lives
   * inside a dialog or any `overflow-hidden` / scrollable ancestor that would
   * otherwise clip an absolutely-positioned panel.
   */
  portal?: boolean
  /**
   * Confine the portaled panel horizontally to the nearest trigger ancestor
   * matching this selector (e.g. a side rail), instead of the whole viewport.
   * Without it a right-aligned popover anchored near a rail's edge spills out
   * over the neighbouring content. Portal-only.
   */
  boundarySelector?: string
  /**
   * Stretch the panel to the boundary element's width (minus the viewport
   * margins) so it spans the container — e.g. a rail-wide filter panel that
   * follows the rail as it resizes. Requires `boundarySelector`; the content
   * should be `w-full`. Portal-only.
   */
  matchBoundaryWidth?: boolean
}

const ALIGN_CLASSES: Record<PopoverAlign, string> = {
  left: 'left-0',
  center: 'left-1/2 -translate-x-1/2',
  right: 'right-0',
}

const SIDE_CLASSES: Record<ResolvedSide, string> = {
  bottom: 'top-full mt-1',
  top: 'bottom-full mb-1',
}

type PopoverAnimation = 'scale' | 'slide'

interface MotionClasses {
  base: string
  open: string
  closed: string
}

const MOTION: Record<PopoverAnimation, Record<ResolvedSide, MotionClasses>> = {
  scale: {
    bottom: {
      base: 'origin-top',
      open: 'scale-y-100 opacity-100',
      closed: 'scale-y-95 opacity-0',
    },
    top: {
      base: 'origin-bottom',
      open: 'scale-y-100 opacity-100',
      closed: 'scale-y-95 opacity-0',
    },
  },
  slide: {
    bottom: {
      base: '',
      open: 'translate-y-0 opacity-100',
      closed: '-translate-y-2 opacity-0',
    },
    top: {
      base: '',
      open: 'translate-y-0 opacity-100',
      closed: 'translate-y-2 opacity-0',
    },
  },
}

function useResolvedSide(
  open: boolean,
  side: PopoverSide,
  triggerRef: React.RefObject<HTMLDivElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
) {
  const [resolvedSide, setResolvedSide] = useState<ResolvedSide>(
    side === 'auto' ? 'bottom' : side,
  )
  useLayoutEffect(() => {
    if (side !== 'auto') {
      setResolvedSide(side)
      return
    }
    if (!open) return
    const trigger = triggerRef.current
    const content = contentRef.current
    if (!trigger || !content) return
    // Re-measure on every change that can move the trigger or grow the panel
    // — notably when a sub-panel mounts after open and changes the height.
    const measure = () => {
      const rect = trigger.getBoundingClientRect()
      const contentHeight = content.offsetHeight
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN_PX
      const spaceAbove = rect.top - VIEWPORT_MARGIN_PX
      setResolvedSide(
        spaceBelow < contentHeight && spaceAbove > spaceBelow
          ? 'top'
          : 'bottom',
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, side, triggerRef, contentRef])
  return resolvedSide
}

function useResolvedAlign(
  open: boolean,
  align: PopoverAlign | 'auto',
  triggerRef: React.RefObject<HTMLDivElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
) {
  const [resolvedAlign, setResolvedAlign] = useState<PopoverAlign>(
    align === 'auto' ? 'left' : align,
  )
  useLayoutEffect(() => {
    if (align !== 'auto') {
      setResolvedAlign(align)
      return
    }
    if (!open) return
    const trigger = triggerRef.current
    const content = contentRef.current
    if (!trigger || !content) return
    // Re-measure when the panel's width changes (e.g. a wide sub-panel mounts
    // after open) so the alignment flips instead of spilling off-screen.
    const measure = () => {
      const rect = trigger.getBoundingClientRect()
      const contentWidth = content.offsetWidth
      const fitsRight =
        rect.left + contentWidth <= window.innerWidth - VIEWPORT_MARGIN_PX
      const fitsLeft = rect.right - contentWidth >= VIEWPORT_MARGIN_PX
      setResolvedAlign(fitsRight ? 'left' : fitsLeft ? 'right' : 'left')
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, align, triggerRef, contentRef])
  return resolvedAlign
}

function useContentWidth(
  enabled: boolean,
  contentRef: React.RefObject<HTMLDivElement | null>,
) {
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    if (!enabled) return
    const update = () => {
      const el = contentRef.current
      if (el) setWidth(el.offsetWidth)
    }
    update()
    const el = contentRef.current
    const observer = new ResizeObserver(update)
    if (el) observer.observe(el)
    window.addEventListener('resize', update)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [enabled, contentRef])
  return width
}

function useTriggerRect(
  enabled: boolean,
  triggerRef: React.RefObject<HTMLDivElement | null>,
) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  useLayoutEffect(() => {
    if (!enabled) return
    const update = () => {
      const el = triggerRef.current
      if (el) setRect(el.getBoundingClientRect())
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [enabled, triggerRef])
  return rect
}

/** The rect of the trigger's nearest ancestor matching `selector`, if any. */
function useBoundaryRect(
  enabled: boolean,
  triggerRef: React.RefObject<HTMLDivElement | null>,
  selector: string | undefined,
) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  useLayoutEffect(() => {
    if (!enabled || !selector) {
      setRect(null)
      return
    }
    const el = triggerRef.current?.closest(selector)
    if (!el) {
      setRect(null)
      return
    }
    const update = () => setRect(el.getBoundingClientRect())
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [enabled, selector, triggerRef])
  return rect
}

/** The panel's left edge for an alignment, before viewport clamping. */
function anchorLeft(rect: DOMRect, align: PopoverAlign, contentWidth: number) {
  if (align === 'right') return rect.right - contentWidth
  if (align === 'center') return rect.left + rect.width / 2 - contentWidth / 2
  return rect.left
}

/**
 * Keep the panel within the viewport (and the boundary element, when given):
 * clamp its left edge so neither side is clipped. On a narrow screen a wide
 * panel ends up pinned to both margins — i.e. visually centered. Skips
 * clamping until the content has been measured.
 */
function clampLeft(
  rawLeft: number,
  contentWidth: number,
  boundary: DOMRect | null,
) {
  if (contentWidth <= 0) return rawLeft
  const minLeft = Math.max(
    VIEWPORT_MARGIN_PX,
    boundary ? boundary.left + VIEWPORT_MARGIN_PX : VIEWPORT_MARGIN_PX,
  )
  const rightEdge = Math.min(
    window.innerWidth - VIEWPORT_MARGIN_PX,
    boundary ? boundary.right - VIEWPORT_MARGIN_PX : Infinity,
  )
  return Math.max(minLeft, Math.min(rawLeft, rightEdge - contentWidth))
}

/** Width/left overrides when the panel spans or is confined to a boundary. */
function boundaryStyle(
  boundary: DOMRect | null,
  matchBoundaryWidth: boolean,
): Pick<CSSProperties, 'width' | 'maxWidth'> & { left?: number } {
  if (!boundary) return {}
  const maxWidth = Math.min(
    window.innerWidth - VIEWPORT_MARGIN_PX * 2,
    boundary.width - VIEWPORT_MARGIN_PX * 2,
  )
  if (!matchBoundaryWidth) return { maxWidth }
  return {
    width: maxWidth,
    maxWidth,
    left: clampLeft(boundary.left + VIEWPORT_MARGIN_PX, maxWidth, boundary),
  }
}

function portalStyle(
  rect: DOMRect | null,
  align: PopoverAlign,
  resolvedSide: ResolvedSide,
  open: boolean,
  contentWidth: number,
  matchTriggerWidth: boolean,
  boundary: DOMRect | null,
  matchBoundaryWidth: boolean,
): CSSProperties {
  if (!rect)
    return { position: 'fixed', top: 0, left: 0, pointerEvents: 'none' }
  const left = clampLeft(
    anchorLeft(rect, align, contentWidth),
    contentWidth,
    boundary,
  )
  const ty = resolvedSide === 'top' ? '-100%' : '0'
  const override = boundaryStyle(boundary, matchBoundaryWidth)
  return {
    position: 'fixed',
    top: resolvedSide === 'top' ? rect.top - GAP_PX : rect.bottom + GAP_PX,
    left: override.left ?? left,
    width: override.width,
    minWidth: matchTriggerWidth ? rect.width : undefined,
    maxWidth: override.maxWidth ?? `calc(100vw - ${VIEWPORT_MARGIN_PX * 2}px)`,
    transform: ty === '0' ? undefined : `translateY(${ty})`,
    // z-80 (popout layer): above the modal layer (z-70) so a popover opened
    // inside a dialog clears it. Matches the portal backdrop below.
    zIndex: 80,
    pointerEvents: open ? 'auto' : 'none',
  }
}

function PanelBody({
  contentRef,
  open,
  resolvedSide,
  animation = 'scale',
  contentClassName,
  children,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>
  open: boolean
  resolvedSide: ResolvedSide
  animation?: PopoverAnimation
  contentClassName?: string
  children: React.ReactNode
}) {
  const motion = MOTION[animation][resolvedSide]
  return (
    <div
      ref={contentRef}
      // Marks this as popover content so surface-aware children (e.g. the
      // Calendar) drop their own `bg-background` and inherit the panel's
      // surface — without it the calendar renders a shade darker than its panel
      // in dark themes (bg-background = canvas, panel = surface).
      data-slot="popover-content"
      className={cn(
        'transition duration-200',
        motion.base,
        open ? motion.open : cn('pointer-events-none', motion.closed),
        contentClassName,
      )}
    >
      {children}
    </div>
  )
}

interface PanelLayerProps {
  open: boolean
  close: () => void
  align: PopoverAlign
  resolvedSide: ResolvedSide
  animation: PopoverAnimation
  contentRef: React.RefObject<HTMLDivElement | null>
  contentClassName?: string
  matchTriggerWidth: boolean
  children: React.ReactNode
}

function InlinePanel({
  open,
  close,
  align,
  resolvedSide,
  animation,
  contentRef,
  contentClassName,
  matchTriggerWidth,
  children,
}: PanelLayerProps) {
  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ display: open ? 'block' : 'none' }}
        onClick={close}
      />
      <div
        className={cn(
          'absolute z-50',
          matchTriggerWidth && 'min-w-full',
          !open && 'pointer-events-none',
          SIDE_CLASSES[resolvedSide],
          ALIGN_CLASSES[align],
        )}
      >
        <PanelBody
          contentRef={contentRef}
          open={open}
          resolvedSide={resolvedSide}
          animation={animation}
          contentClassName={contentClassName}
        >
          {children}
        </PanelBody>
      </div>
    </>
  )
}

function PortalPanel({
  open,
  close,
  align,
  resolvedSide,
  animation,
  contentRef,
  contentClassName,
  matchTriggerWidth,
  rect,
  contentWidth,
  boundary,
  matchBoundaryWidth,
  children,
}: PanelLayerProps & {
  rect: DOMRect | null
  contentWidth: number
  boundary: DOMRect | null
  matchBoundaryWidth: boolean
}) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return null

  return createPortal(
    <>
      <div
        // z-80 (popout layer): portaled popovers float above the modal layer
        // (dialogs, z-70) and the detail sidebar (z-60) regardless of DOM order.
        // See the z-index scale in app/styles/index.css.
        className="fixed inset-0 z-[80]"
        style={{ display: open ? 'block' : 'none', pointerEvents: 'auto' }}
        onClick={close}
      />
      <div
        style={portalStyle(
          rect,
          align,
          resolvedSide,
          open,
          contentWidth,
          matchTriggerWidth,
          boundary,
          matchBoundaryWidth,
        )}
      >
        <PanelBody
          contentRef={contentRef}
          open={open}
          resolvedSide={resolvedSide}
          animation={animation}
          contentClassName={contentClassName}
        >
          {children}
        </PanelBody>
      </div>
    </>,
    document.body,
  )
}

function resolvePanel(
  children: PopoverProps['children'],
  close: () => void,
  open: boolean,
) {
  return typeof children === 'function' ? children(close, open) : children
}

function useControlledOpen(
  defaultOpen: boolean,
  controlledOpen: boolean | undefined,
  onOpenChange?: (open: boolean) => void,
) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }
  return { open, setOpen }
}

export function Popover({
  trigger,
  children,
  align = 'center',
  side = 'bottom',
  animation = 'scale',
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  contentClassName,
  wrapperClassName,
  matchTriggerWidth = true,
  portal = false,
  boundarySelector,
  matchBoundaryWidth = false,
}: PopoverProps) {
  const { open, setOpen } = useControlledOpen(
    defaultOpen,
    controlledOpen,
    onOpenChange,
  )
  const close = () => setOpen(false)

  // Escape closes the top-most open layer only: the shared stack keeps an
  // open popover above whatever surface (panel, dialog) opened it. The
  // callback must stay referentially stable — re-pushing on every render
  // would hoist this popover over layers opened after it.
  const setOpenRef = useRef(setOpen)
  useEffect(() => {
    setOpenRef.current = setOpen
  })
  const escapeClose = useCallback(() => setOpenRef.current(false), [])
  useEscapeStack(open, escapeClose)

  const triggerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const resolvedSide = useResolvedSide(open, side, triggerRef, contentRef)
  const resolvedAlign = useResolvedAlign(open, align, triggerRef, contentRef)
  const portalOpen = portal && open
  const rect = useTriggerRect(portalOpen, triggerRef)
  const contentWidth = useContentWidth(portalOpen, contentRef)
  const boundary = useBoundaryRect(portalOpen, triggerRef, boundarySelector)

  const panel = resolvePanel(children, close, open)

  return (
    <div className={cn('relative inline-flex', wrapperClassName)}>
      <div
        ref={triggerRef}
        className={cn('inline-flex', wrapperClassName)}
        onClick={() => setOpen(!open)}
      >
        {trigger(open)}
      </div>

      {portal ? (
        <PortalPanel
          open={open}
          close={close}
          align={resolvedAlign}
          resolvedSide={resolvedSide}
          animation={animation}
          contentRef={contentRef}
          contentClassName={contentClassName}
          matchTriggerWidth={matchTriggerWidth}
          rect={rect}
          contentWidth={contentWidth}
          boundary={boundary}
          matchBoundaryWidth={matchBoundaryWidth}
        >
          {panel}
        </PortalPanel>
      ) : (
        <InlinePanel
          open={open}
          close={close}
          align={resolvedAlign}
          resolvedSide={resolvedSide}
          animation={animation}
          contentRef={contentRef}
          contentClassName={contentClassName}
          matchTriggerWidth={matchTriggerWidth}
        >
          {panel}
        </InlinePanel>
      )}
    </div>
  )
}

interface PopoverTriggerButtonProps {
  children: React.ReactNode
  open?: boolean
  className?: string
}

export function PopoverTriggerButton({
  children,
  open,
  className,
}: PopoverTriggerButtonProps) {
  return (
    <Button
      variant="outline"
      size="xs"
      className={cn(
        'focus:outline-none',
        open && 'ring-2 ring-primary ring-offset-2',
        className,
      )}
    >
      {children}
      <ChevronDownIcon
        className={cn(
          'size-2.5 transition-transform duration-200',
          open ? 'rotate-180' : 'rotate-0',
        )}
      />
    </Button>
  )
}
